import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

import {
  BoardAnnouncement,
  BoardContact,
  BoardDocument,
  BoardDocumentType,
  BoardToolkitSection,
  CreateBoardDocumentDto,
  UpdateBoardDocumentDto,
  CreateBoardContactDto,
  UpdateBoardContactDto,
  CreateBoardAnnouncementDto,
  UpdateBoardAnnouncementDto,
  CreateBoardToolkitSectionDto,
  UpdateBoardToolkitSectionDto,
} from '../models/board-portal.model';

/**
 * BoardPortalService
 *
 * HTTP client for the private Board Portal API (/api/board-portal).
 * Read methods are available to all board members.
 * Write/Delete methods are restricted to Administrators (enforced server-side).
 */
@Injectable({ providedIn: 'root' })
export class BoardPortalService {
  private readonly apiUrl = environment.apiUrl.replace(/\/$/, '');
  private readonly base = `${this.apiUrl}/api/board-portal`;

  constructor(private readonly http: HttpClient) {}

  // --- Documents -------------------------------------------------------------

  getAllDocuments(documentType?: BoardDocumentType | string): Observable<BoardDocument[]> {
    let params = new HttpParams();
    if (documentType) params = params.set('type', documentType);
    return this.http.get<BoardDocument[]>(`${this.base}/documents`, { params });
  }

  getDocument(id: string): Observable<BoardDocument> {
    return this.http.get<BoardDocument>(`${this.base}/documents/${id}`);
  }

  createDocument(dto: CreateBoardDocumentDto): Observable<BoardDocument> {
    return this.http.post<BoardDocument>(`${this.base}/documents`, dto);
  }

  updateDocument(id: string, dto: UpdateBoardDocumentDto): Observable<BoardDocument> {
    return this.http.put<BoardDocument>(`${this.base}/documents/${id}`, dto);
  }

  deleteDocument(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/documents/${id}`);
  }

  // --- Contacts --------------------------------------------------------------

  getAllContacts(): Observable<BoardContact[]> {
    return this.http.get<BoardContact[]>(`${this.base}/contacts`);
  }

  getContact(id: string): Observable<BoardContact> {
    return this.http.get<BoardContact>(`${this.base}/contacts/${id}`);
  }

  createContact(dto: CreateBoardContactDto): Observable<BoardContact> {
    return this.http.post<BoardContact>(`${this.base}/contacts`, dto);
  }

  updateContact(id: string, dto: UpdateBoardContactDto): Observable<BoardContact> {
    return this.http.put<BoardContact>(`${this.base}/contacts/${id}`, dto);
  }

  deleteContact(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/contacts/${id}`);
  }

  // --- Announcements ---------------------------------------------------------

  getAllAnnouncements(): Observable<BoardAnnouncement[]> {
    return this.http.get<BoardAnnouncement[]>(`${this.base}/announcements`);
  }

  getAnnouncement(id: string): Observable<BoardAnnouncement> {
    return this.http.get<BoardAnnouncement>(`${this.base}/announcements/${id}`);
  }

  createAnnouncement(dto: CreateBoardAnnouncementDto): Observable<BoardAnnouncement> {
    return this.http.post<BoardAnnouncement>(`${this.base}/announcements`, dto);
  }

  updateAnnouncement(id: string, dto: UpdateBoardAnnouncementDto): Observable<BoardAnnouncement> {
    return this.http.put<BoardAnnouncement>(`${this.base}/announcements/${id}`, dto);
  }

  deleteAnnouncement(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/announcements/${id}`);
  }

  // --- Toolkit Sections ------------------------------------------------------

  getAllToolkitSections(): Observable<BoardToolkitSection[]> {
    return this.http.get<BoardToolkitSection[]>(`${this.base}/toolkit`);
  }

  getToolkitSection(id: string): Observable<BoardToolkitSection> {
    return this.http.get<BoardToolkitSection>(`${this.base}/toolkit/${id}`);
  }

  createToolkitSection(dto: CreateBoardToolkitSectionDto): Observable<BoardToolkitSection> {
    return this.http.post<BoardToolkitSection>(`${this.base}/toolkit`, dto);
  }

  updateToolkitSection(id: string, dto: UpdateBoardToolkitSectionDto): Observable<BoardToolkitSection> {
    return this.http.put<BoardToolkitSection>(`${this.base}/toolkit/${id}`, dto);
  }

  deleteToolkitSection(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/toolkit/${id}`);
  }
}

